import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Home, DollarSign, TrendingUp, MapPin, FileText, Calculator, ExternalLink, Search, Building } from 'lucide-react';
import { toast } from 'sonner';

const COUNTIES = [
  'Collin County', 'Dallas County', 'Denton County', 'Tarrant County', 
  'Rockwall County', 'Ellis County', 'Kaufman County'
];

const CITIES = [
  'Frisco', 'Plano', 'McKinney', 'Allen', 'Dallas', 'Fort Worth', 
  'Arlington', 'Mansfield', 'Richardson', 'Irving', 'Garland', 
  'Grand Prairie', 'Lewisville', 'Flower Mound', 'Denton'
];

const HomeValuation = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('calculator');
  
  // Calculator state
  const [calcData, setCalcData] = useState({
    address: '',
    city: '',
    sqft: '',
    bedrooms: '',
    bathrooms: '',
    yearBuilt: '',
    lotSize: '',
    condition: ''
  });
  
  const [valuation, setValuation] = useState(null);
  const [loading, setLoading] = useState(false);

  // Tax records state
  const [taxSearch, setTaxSearch] = useState({
    address: '',
    county: 'Dallas County'
  });

  const calculateValue = () => {
    if (!calcData.sqft || !calcData.city) {
      toast.error('Please fill in required fields (Square Footage and City)');
      return;
    }

    setLoading(true);

    // Simulate calculation (in production, this would call your backend API)
    setTimeout(() => {
      const sqft = parseInt(calcData.sqft);
      const beds = parseInt(calcData.bedrooms) || 3;
      const baths = parseFloat(calcData.bathrooms) || 2;
      const year = parseInt(calcData.yearBuilt) || 2010;
      
      // Base price per SF by city (DFW averages)
      const basePricePerSF = {
        'Frisco': 220,
        'Plano': 210,
        'McKinney': 195,
        'Allen': 205,
        'Dallas': 185,
        'Fort Worth': 175,
        'Arlington': 170,
        'Mansfield': 180,
        'Richardson': 190,
        'Irving': 165,
        'Garland': 160,
        'Grand Prairie': 155,
        'Lewisville': 175,
        'Flower Mound': 200,
        'Denton': 170
      };

      let pricePerSF = basePricePerSF[calcData.city] || 180;
      
      // Adjustments
      if (year > 2020) pricePerSF *= 1.15; // Newer homes
      else if (year < 2000) pricePerSF *= 0.90; // Older homes
      
      if (calcData.condition === 'excellent') pricePerSF *= 1.10;
      else if (calcData.condition === 'poor') pricePerSF *= 0.85;
      
      const baseValue = sqft * pricePerSF;
      const lowValue = baseValue * 0.93;
      const highValue = baseValue * 1.07;

      setValuation({
        estimatedValue: Math.round(baseValue),
        lowRange: Math.round(lowValue),
        highRange: Math.round(highValue),
        pricePerSF: Math.round(pricePerSF),
        marketTrend: calcData.city === 'Frisco' || calcData.city === 'Plano' ? '+5.2%' : '+3.8%',
        daysOnMarket: calcData.city === 'Frisco' ? 18 : 25,
        competitorCount: Math.floor(Math.random() * 15) + 8
      });
      
      setLoading(false);
      toast.success('Home valuation calculated!');
    }, 1500);
  };

  const openTaxRecords = () => {
    if (!taxSearch.address || !taxSearch.county) {
      toast.error('Please enter address and select county');
      return;
    }

    const countyLinks = {
      'Dallas County': 'https://www.dallascad.org/SearchAddr.aspx',
      'Collin County': 'https://www.collincad.org/propertysearch',
      'Tarrant County': 'https://www.tad.org/SearchAddr.aspx',
      'Denton County': 'https://www.dentoncad.com/SearchAddr.aspx'
    };

    const link = countyLinks[taxSearch.county] || countyLinks['Dallas County'];
    window.open(link, '_blank');
    toast.success('Opening county tax records...');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>
                Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup>
              </span>
            </div>
            <Button onClick={() => navigate(user ? '/dashboard' : '/login')} className="bg-orange-600 hover:bg-orange-700">
              {user ? 'Dashboard' : 'Sign In'}
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Free Home Valuation Tools
          </h1>
          <p className="text-xl text-gray-700 mb-2">
            Discover your DFW home's value instantly - No signup required
          </p>
          <p className="text-base text-gray-600">
            Access tax records, calculate price per square foot, and see market comparables
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center space-x-4 mb-8">
          <Button
            onClick={() => setActiveTab('calculator')}
            variant={activeTab === 'calculator' ? 'default' : 'outline'}
            className={activeTab === 'calculator' ? 'bg-orange-600' : 'border-orange-300'}
          >
            <Calculator className="w-4 h-4 mr-2" />
            Value Calculator
          </Button>
          <Button
            onClick={() => setActiveTab('tax-records')}
            variant={activeTab === 'tax-records' ? 'default' : 'outline'}
            className={activeTab === 'tax-records' ? 'bg-orange-600' : 'border-orange-300'}
          >
            <FileText className="w-4 h-4 mr-2" />
            Tax Records
          </Button>
          <Button
            onClick={() => setActiveTab('market-data')}
            variant={activeTab === 'market-data' ? 'default' : 'outline'}
            className={activeTab === 'market-data' ? 'bg-orange-600' : 'border-orange-300'}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Market Data
          </Button>
        </div>

        {/* Value Calculator Tab */}
        {activeTab === 'calculator' && (
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Instant Home Value Estimate</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="address">Property Address</Label>
                  <Input
                    id="address"
                    value={calcData.address}
                    onChange={(e) => setCalcData({...calcData, address: e.target.value})}
                    placeholder="123 Main St"
                  />
                </div>
                
                <div>
                  <Label htmlFor="city" className="required">City *</Label>
                  <Select value={calcData.city} onValueChange={(val) => setCalcData({...calcData, city: val})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {CITIES.map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="sqft" className="required">Square Footage *</Label>
                  <Input
                    id="sqft"
                    type="number"
                    value={calcData.sqft}
                    onChange={(e) => setCalcData({...calcData, sqft: e.target.value})}
                    placeholder="2000"
                  />
                </div>

                <div>
                  <Label htmlFor="bedrooms">Bedrooms</Label>
                  <Input
                    id="bedrooms"
                    type="number"
                    value={calcData.bedrooms}
                    onChange={(e) => setCalcData({...calcData, bedrooms: e.target.value})}
                    placeholder="3"
                  />
                </div>

                <div>
                  <Label htmlFor="bathrooms">Bathrooms</Label>
                  <Input
                    id="bathrooms"
                    type="number"
                    step="0.5"
                    value={calcData.bathrooms}
                    onChange={(e) => setCalcData({...calcData, bathrooms: e.target.value})}
                    placeholder="2.5"
                  />
                </div>

                <div>
                  <Label htmlFor="yearBuilt">Year Built</Label>
                  <Input
                    id="yearBuilt"
                    type="number"
                    value={calcData.yearBuilt}
                    onChange={(e) => setCalcData({...calcData, yearBuilt: e.target.value})}
                    placeholder="2010"
                  />
                </div>

                <div>
                  <Label htmlFor="lotSize">Lot Size (sq ft)</Label>
                  <Input
                    id="lotSize"
                    type="number"
                    value={calcData.lotSize}
                    onChange={(e) => setCalcData({...calcData, lotSize: e.target.value})}
                    placeholder="8000"
                  />
                </div>

                <div>
                  <Label htmlFor="condition">Property Condition</Label>
                  <Select value={calcData.condition} onValueChange={(val) => setCalcData({...calcData, condition: val})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="average">Average</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                onClick={calculateValue}
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-lg py-6"
              >
                {loading ? 'Calculating...' : 'Calculate Home Value'}
              </Button>

              {/* Valuation Results */}
              {valuation && (
                <div className="mt-8 p-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Estimated Home Value</h3>
                  
                  <div className="text-center mb-6">
                    <div className="text-5xl font-bold text-orange-600 mb-2" style={{ fontFamily: 'Playfair Display' }}>
                      ${valuation.estimatedValue.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">
                      Range: ${valuation.lowRange.toLocaleString()} - ${valuation.highRange.toLocaleString()}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg">
                      <div className="text-sm text-gray-600">Price/SF</div>
                      <div className="text-xl font-bold text-gray-900">${valuation.pricePerSF}</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <div className="text-sm text-gray-600">Market Trend</div>
                      <div className="text-xl font-bold text-green-600">{valuation.marketTrend}</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <div className="text-sm text-gray-600">Avg Days</div>
                      <div className="text-xl font-bold text-gray-900">{valuation.daysOnMarket}</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <div className="text-sm text-gray-600">Competitors</div>
                      <div className="text-xl font-bold text-gray-900">{valuation.competitorCount}</div>
                    </div>
                  </div>

                  <div className="mt-6 text-center">
                    <Button onClick={() => navigate('/create')} className="bg-orange-600 hover:bg-orange-700">
                      List Your Home for Free
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Tax Records Tab */}
        {activeTab === 'tax-records' && (
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Access Property Tax Records</h2>
              <p className="text-gray-600 mb-6">
                View official property tax records, assessed values, and property details from DFW county appraisal districts.
              </p>

              <div className="space-y-6">
                <div>
                  <Label htmlFor="tax-address">Property Address</Label>
                  <Input
                    id="tax-address"
                    value={taxSearch.address}
                    onChange={(e) => setTaxSearch({...taxSearch, address: e.target.value})}
                    placeholder="123 Main St, Dallas, TX"
                  />
                </div>

                <div>
                  <Label htmlFor="county">County</Label>
                  <Select value={taxSearch.county} onValueChange={(val) => setTaxSearch({...taxSearch, county: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTIES.map(county => (
                        <SelectItem key={county} value={county}>{county}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={openTaxRecords} className="w-full bg-orange-600 hover:bg-orange-700">
                  <Search className="w-4 h-4 mr-2" />
                  Search Tax Records
                </Button>

                {/* Quick Links */}
                <div className="mt-8 border-t pt-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Direct Links to County Appraisal Districts:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Button variant="outline" onClick={() => window.open('https://www.dallascad.org', '_blank')} className="justify-start">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Dallas County CAD
                    </Button>
                    <Button variant="outline" onClick={() => window.open('https://www.collincad.org', '_blank')} className="justify-start">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Collin County CAD
                    </Button>
                    <Button variant="outline" onClick={() => window.open('https://www.tad.org', '_blank')} className="justify-start">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Tarrant County CAD
                    </Button>
                    <Button variant="outline" onClick={() => window.open('https://www.dentoncad.com', '_blank')} className="justify-start">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Denton County CAD
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Market Data Tab */}
        {activeTab === 'market-data' && (
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">DFW Real Estate Market Data</h2>
              
              <div className="space-y-6">
                {/* Price Per SF by City */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Average Price Per Square Foot</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      { city: 'Frisco', price: 220 },
                      { city: 'Plano', price: 210 },
                      { city: 'Allen', price: 205 },
                      { city: 'Flower Mound', price: 200 },
                      { city: 'McKinney', price: 195 },
                      { city: 'Richardson', price: 190 },
                      { city: 'Dallas', price: 185 },
                      { city: 'Mansfield', price: 180 },
                      { city: 'Fort Worth', price: 175 }
                    ].map(item => (
                      <div key={item.city} className="p-4 bg-slate-50 rounded-lg">
                        <div className="text-sm text-gray-600">{item.city}</div>
                        <div className="text-xl font-bold text-orange-600">${item.price}/SF</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Market Trends */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Market Trends</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Median Home Price</span>
                        <TrendingUp className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="text-2xl font-bold text-gray-900">$425K</div>
                      <div className="text-xs text-green-600">+4.2% YoY</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Days on Market</span>
                        <Building className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="text-2xl font-bold text-gray-900">22 days</div>
                      <div className="text-xs text-blue-600">Fast market</div>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Inventory</span>
                        <Home className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="text-2xl font-bold text-gray-900">2.1 mo</div>
                      <div className="text-xs text-orange-600">Seller's market</div>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="border-t pt-6 text-center">
                  <p className="text-gray-700 mb-4">
                    Ready to list your home and take advantage of the hot DFW market?
                  </p>
                  <Button onClick={() => navigate('/create')} className="bg-orange-600 hover:bg-orange-700">
                    List Your Home for Free
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Benefits Section */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">100% Free</h3>
            <p className="text-sm text-gray-600">No signup or credit card required</p>
          </Card>
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Accurate Data</h3>
            <p className="text-sm text-gray-600">Based on real DFW market trends</p>
          </Card>
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Local Focus</h3>
            <p className="text-sm text-gray-600">Specialized for Dallas-Fort Worth</p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HomeValuation;
